import { getToken } from "../storage/authStorage";
import { AccountTreeResponseDTO } from "../models/AccountTreeDTOs";
import { BankGlPreset } from "../../domain/entities/BankGlPickerEntity";
import { API_BASE_URL } from "../../../config/api";

class APIError extends Error {
  public status: number;
  public data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

function getBankGlTreeEndpoint(preset: BankGlPreset): string {
  switch (preset) {
    case "expense":
      return `${API_BASE_URL}/api/gl-maps/expense-gls`;
    case "income":
      return `${API_BASE_URL}/api/gl-maps/by-account-type/income`;
    case "equity":
      return `${API_BASE_URL}/api/gl-maps/by-account-type/equity`;
    case "credits_refund":
      return `${API_BASE_URL}/api/gl-maps/credits-or-refund-received-accounts`;
    case "deposit_from_other":
      return `${API_BASE_URL}/api/gl-maps/customer-deposit-from-other-accounts`;
    case "transfer_to_other":
      return `${API_BASE_URL}/api/gl-maps/vendor-transfer-to-other-accounts`;
    default:
      throw new APIError(`Unknown bank GL preset: ${preset}`, 500);
  }
}

export interface FetchBankGlTreeOptions {
  preset: BankGlPreset;
  bankAccountId?: string;
  search?: string;
  accountType?: string;
  limit?: number;
  offset?: number;
  selectedAccountId?: string;
}

export const fetchBankGlTree = async (
  options: FetchBankGlTreeOptions,
): Promise<AccountTreeResponseDTO> => {
  const {
    preset,
    bankAccountId,
    search,
    accountType,
    limit = 50,
    offset = 0,
    selectedAccountId,
  } = options;

  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const params = new URLSearchParams();
    params.append("format", "tree");

    if (bankAccountId) {
      params.append("bank_account_id", bankAccountId);
    }

    if (search) {
      params.append("search", search);
      params.append("limit", limit.toString());
      params.append("offset", offset.toString());
    } else if (accountType) {
      params.append("account_type", accountType);
      if (selectedAccountId) {
        params.append("selected_account_id", selectedAccountId);
      }
    } else if (selectedAccountId) {
      params.append("selected_account_id", selectedAccountId);
    }

    const response = await fetch(
      `${getBankGlTreeEndpoint(preset)}?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch bank GL tree: ${response.statusText}`,
        response.status,
      );
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching bank GL tree:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};
